/**
 * Global axios interceptor — must be imported in index.js before anything else.
 * Attaches the Keycloak Bearer token to every outgoing request.
 */
import axios from "axios";
import keycloak from "./keycloak";
import { ensureAccessToken, accessTokenFreshSeconds } from "./keycloakRefresh";

function isIntakeLongRequest(config) {
  const url = String(config?.url || "");
  const method = String(config?.method || "").toLowerCase();
  return (
    config?.intakeLongRunning === true ||
    (method === "post" && /\/intake\/[^/]+\/upload\b/i.test(url)) ||
    (method === "post" && /\/intake\/[^/]+\/documents\b/i.test(url))
  );
}

axios.interceptors.request.use(async (config) => {
  if (!keycloak.authenticated) return config;
  const minValidity = isIntakeLongRequest(config) ? 120 : 30;
  try {
    await ensureAccessToken(minValidity);
  } catch {
    if (!accessTokenFreshSeconds(5)) {
      keycloak.login();
      return Promise.reject(new Error("Token refresh failed — re-login required"));
    }
  }
  if (keycloak.token) {
    config.headers.Authorization = `Bearer ${keycloak.token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config;
    if (!original || original._retry) {
      return Promise.reject(error);
    }
    const status = error?.response?.status;
    if (status !== 401 || !keycloak.authenticated) {
      return Promise.reject(error);
    }
    original._retry = true;
    try {
      await ensureAccessToken(60);
      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${keycloak.token}`;
      return axios(original);
    } catch (refreshErr) {
      if (!accessTokenFreshSeconds(5)) {
        keycloak.login();
      }
      return Promise.reject(refreshErr);
    }
  }
);
